// Prévenir à temps d'une absence, le désistement tardif et l'absence sans prévenir (D-148) : les mots, logique pure (testée
// dans desist.test.ts). La base décide de tout le reste (la limite, le compte, qui voit quoi) : migration
// 20261105400000_desistement_tardif.sql.
import { agree, type TeamGender } from './labels';

/** Les raisons : d'un toucher, jamais un détail médical (D-009). */
export type ReasonKey = 'sick' | 'school' | 'family' | 'transport' | 'forgot' | 'other';
/** Après la limite (feuille du désistement tardif) : pas « oubli », elle prévient. */
export const LATE_REASONS: ReasonKey[] = ['sick', 'school', 'family', 'transport', 'other'];
/** « Tout va bien ? » après une absence sans prévenir. */
export const ASK_REASONS: ReasonKey[] = ['sick', 'school', 'family', 'transport', 'forgot', 'other'];

/** La raison en clair, accordée ; un mineur dit « École » (jamais « travail », D-103) ; « J’ai oublié de prévenir » pour soi. */
export function reasonLabel(k: ReasonKey | string | null | undefined, g: TeamGender, o: { minor?: boolean; self?: boolean } = {}): string {
  switch (k) {
    case 'sick': return agree(g, 'Malade ou blessé', 'Malade ou blessée');
    case 'school': return o.minor ? 'École' : 'École ou travail';
    case 'family': return 'Famille';
    case 'transport': return 'Transport';
    case 'forgot': return o.self ? 'J’ai oublié de prévenir' : 'Oubli de prévenir';
    case 'other': return 'Autre';
    default: return '';
  }
}

/** « 1er », « 2e », « 3e » (un désistement). */
export const ordinal = (n: number): string => (n === 1 ? '1er' : `${n}e`);

/** « a », « a et b », « a, b et c ». */
export function joinFr(list: readonly string[]): string {
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} et ${list[list.length - 1]}`;
}

const NEAR = ['ce soir', 'aujourd’hui', 'hier', 'demain'];
const WEEKDAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
/** Le moment donné par la base (« ce soir », « hier », « samedi », « samedi 7 novembre ») précédé de « de » : « de ce soir »,
 *  « d’hier », « de samedi », « du samedi 7 novembre ». */
export function deSoon(soon: string): string {
  if (soon === 'aujourd’hui' || soon === 'hier') return `d’${soon}`;
  if (NEAR.includes(soon) || WEEKDAYS.includes(soon)) return `de ${soon}`;
  return `du ${soon}`;
}
/** Pour un surtitre : « de ce soir », « de ce mercredi », « du samedi 7 novembre ». */
export const deThisSoon = (soon: string): string => (WEEKDAYS.includes(soon) ? `de ce ${soon}` : deSoon(soon));
/** « à l’entraînement ce soir », « à l’entraînement de ce mercredi », « au match de samedi 7 novembre ». */
export function atWhen(kind: string, soon: string): string {
  const base = kind === 'match' ? 'au match' : 'à l’entraînement';
  if (NEAR.includes(soon)) return `${base} ${soon}`;
  if (WEEKDAYS.includes(soon)) return `${base} de ce ${soon}`;
  return `${base} du ${soon}`;
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** La feuille du désistement tardif : le contexte, puis le message chaleureux (merci, la limite, pourquoi ça compte). */
export function lateCopy(o: { kind: string; soon: string; hm: string; limit: string; coach: string | null; g: TeamGender; self: boolean; name: string;
  /** Le genre de la personne (sa fiche, sinon son équipe) : « compte sur elle », « Malade ou blessée ». Sans : celui de l'équipe. */
  pg?: TeamGender }) {
  const coach = o.coach ?? 'Le coach';
  const pg = o.pg ?? o.g;
  const what = o.kind === 'match' ? `l’équipe ${deSoon(o.soon)}` : `les groupes ${deSoon(o.soon)}`;
  const mates = agree(o.g, 'coéquipiers', 'coéquipières');
  return {
    context: `${cap(o.soon)} · ${o.kind === 'match' ? 'match' : 'entraînement'} · ${o.hm} · désistement tardif`,
    thanks: 'Merci de prévenir, même tard.',
    limit: `La limite, c’était ${o.limit}.`,
    why: o.self
      ? `${coach} a déjà préparé ${what}, et tes ${mates} comptent sur toi : dis-lui ce qui se passe, ça l’aide à réorganiser.`
      : `${coach} a déjà préparé ${what}, et les ${mates} de ${o.name} comptent sur ${agree(pg, 'lui', 'elle')} : dis-lui ce qui se passe, ça l’aide à réorganiser.`,
    question: o.self ? 'Ce qui t’empêche de venir' : `Ce qui empêche ${o.name} de venir`,
    foot: `« ${agree(pg, 'Malade ou blessé', 'Malade ou blessée')} » ne compte pas comme un désistement tardif, et aucun détail médical n’est demandé.`,
  };
}

/** Le message « Tout va bien ? » sur l'accueil : soi, ou un parent pour son enfant. */
export function askCopy(o: { self: boolean; name: string; coach: string | null; kind: string; soon: string; hm: string; label: string; team: string;
  minor: boolean; hasAccount: boolean; g: TeamGender }) {
  const coach = o.coach ?? 'le coach';
  const what = o.kind === 'match' ? `Match ${deThisSoon(o.soon)}` : `Entraînement ${deThisSoon(o.soon)}`;
  return {
    eyebrow: o.self ? `${what} · ${o.hm}` : `${o.name} · ${o.kind === 'match' ? 'match' : `entraînement ${o.team}`} ${deThisSoon(o.soon)} · ${o.hm}`,
    title: o.self ? `Tout va bien, ${o.name} ?` : `Tout va bien pour ${o.name} ?`,
    text: o.self
      ? `Tu n’étais pas ${atWhen(o.kind, o.soon)} et ${coach} n’avait pas de nouvelles. Dis-lui ce qui s’est passé, en un toucher :`
      : `${o.name} n’était pas ${atWhen(o.kind, o.soon)} et ${coach} n’avait pas de nouvelles. Dis-lui ce qui s’est passé, en un toucher :`,
    field: `Un mot pour ${coach} (facultatif)`,
    placeholder: o.minor || !o.self ? 'ex. rendez-vous chez le dentiste' : 'ex. bus supprimé',
    /** Pour un mineur, lui et ses parents reçoivent le message : une seule réponse suffit. */
    foot: !o.self && o.hasAccount ? `${o.name} voit aussi ce message dans son app : une seule réponse suffit.`
      : o.self && o.minor ? 'Tes parents voient aussi ce message : une seule réponse suffit.'
      : `Prévenir, même au dernier moment, aide ${coach} à organiser la séance et ${o.self ? 'tes' : 'ses'} ${agree(o.g, 'coéquipiers', 'coéquipières')} à s’adapter.`,
    both: (!o.self && o.hasAccount) || (o.self && o.minor),
  };
}

/** « prévenue tard », « prévenu tard » : au genre de la personne. */
export const lateTag = (g: TeamGender | string | null | undefined): string => (g === 'F' ? 'prévenue tard' : 'prévenu tard');

/** Le retour après un désistement tardif : « Marc est prévenu. », « Inès est prévenue. » ; le genre du coach inconnu (pas de fiche
 *  au club) : une tournure neutre, « Marc a ton message. » ; pas de coach : « Le coach est prévenu. ». */
export function coachTold(coach: string | null, coachG: string | null | undefined): string {
  if (!coach) return 'Le coach est prévenu.';
  if (coachG === 'F') return `${coach} est prévenue.`;
  if (coachG === 'M') return `${coach} est prévenu.`;
  return `${coach} a ton message.`;
}

/** La carte « Faire le point avec … ? » : la phrase du compte. */
export const pointLead = (count: number): string =>
  `${ordinal(count)} désistement tardif de la saison. Rien d’automatique : un mot en tête-à-tête suffit souvent.`;

/** Le résumé de la rubrique « Prévenir d’une absence » : « Chaque semaine, avant mardi 20h00 », « 24 h avant une séance, 48 h avant un match ». */
export function limitSummary(s: { mode: 'weekly' | 'delay'; weekday: number; at_time: string; training_h: number; match_h: number }): string {
  if (s.mode === 'weekly') return `Chaque semaine, avant ${WEEKDAYS[s.weekday - 1]} ${s.at_time.replace(':', 'h')}`;
  return `${s.training_h} h avant une séance, ${s.match_h} h avant un match`;
}
/** Le résumé de la rubrique « Désistements tardifs ». */
export const lateSummary = (threshold: number, ask: boolean): string =>
  `Faire le point au ${ordinal(threshold)} de la saison · le médical ne compte pas${ask ? '' : ' · sans message après une absence sans prévenir'}`;

/** Les heures proposées pour la limite « chaque semaine » : de 7 h à 23 h 30, toutes les demi-heures (plus celle déjà réglée). */
export const LIMIT_TIMES: string[] = Array.from({ length: 34 }, (_, i) => `${String(7 + Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
export const limitTimes = (current: string): string[] => (LIMIT_TIMES.includes(current) ? LIMIT_TIMES : [...LIMIT_TIMES, current].sort());
